<template>
  <LabeledSelect
    :model-value="model"
    :options="options"
    :reduce="reduce"
    :get-option-label="getOptionLabel"
    :get-option-key="getOptionKey"
    :filter-by="filterBy"
    :create-option="createOption"
    :selectable="selectable"
    :placeholder="placeholder"
    :disabled="disabled"
    :label="$t('other.form.email')"
    taggable
    push-tags
    clearable
    @update:model-value="onInput"
    :class="props.inputClass"
  >
    <template #option="opt">
      <div v-if="opt.isNew" class="email-option email-option--new">
        <template v-if="isValidEmail(opt.email)">Add “{{ opt.email }}”</template>
        <template v-else>Type a full email address to add it</template>
      </div>
      <div v-else class="email-option">
        <span class="email-option__email">{{ opt.email }}</span>
        <span v-if="opt.name" class="email-option__name">{{ opt.name }}</span>
        <span v-if="opt.member?.account.pending" class="email-option__pending">Pending</span>
      </div>
    </template>

    <template #no-options>
      No member found. Type an email address to add it.
    </template>
  </LabeledSelect>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import 'vue-select/dist/vue-select.css';
import { MemberDetails } from '@/types/member';
import LabeledSelect from '../global/LabeledSelect.vue';

/**
 * Internal option shape. Existing members carry their MemberDetails;
 * options typed by the user have `isNew: true` and no member.
 */
type EmailOption = {
  email: string
  name: string
  member: MemberDetails | null
  isNew: boolean
}

const props = withDefaults(
  defineProps<{
    members: MemberDetails[]
    placeholder?: string
    disabled?: boolean,
    inputClass?: string,
  }>(),
  { placeholder: 'Select or type an email', disabled: false },
)

// v-model holds the email string (or null when cleared)
const model = defineModel<string | null>({ default: null })

const emit = defineEmits<{
  // Fired on every change: tells the parent whether the email belongs to an existing member
  (e: 'change', payload: { email: string; member: MemberDetails | null } | null): void
}>()

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const normalize = (email: string) => email.trim().toLowerCase()
const isValidEmail = (email: string) => EMAIL_RE.test(email)

const options = computed<EmailOption[]>(() =>
  props?.members?.map((m) => {
    const info = m.account.personal_information
    return {
      email: info.email,
      name: [info.first_name, info.last_name].filter(Boolean).join(' '),
      member: m,
      isNew: false,
    }
  }),
)

/** Called by vue-select when the user types something that matches no option */
const createOption = (search: string): EmailOption => ({
  email: normalize(search),
  name: '',
  member: null,
  isNew: true,
})

/** Unique key: used to detect duplicates, so "John@X.com" won't be created if "john@x.com" exists */
const getOptionKey = (opt: EmailOption | string) =>
  normalize(typeof opt === 'string' ? opt : opt.email)

const getOptionLabel = (opt: EmailOption | string) =>
  typeof opt === 'string' ? opt : opt.email

/** Search on email AND name */
const filterBy = (opt: EmailOption, _label: string, search: string) => {
  const q = search.toLowerCase()
  return opt.email.toLowerCase().includes(q) || opt.name.toLowerCase().includes(q)
}

/** Existing options are always selectable; new ones only if they look like an email */
const selectable = (opt: EmailOption) => !opt.isNew || isValidEmail(opt.email)

const reduce = (opt: EmailOption) => opt.email

const onInput = (email: string | null) => {
  model.value = email
  if (!email) {
    emit('change', null)
    return
  }
  const match = options.value.find((o) => normalize(o.email) === normalize(email))
  emit('change', { email, member: match?.member ?? null })
}
</script>

<style scoped lang="scss">

.email-option {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.email-option__name {
  font-size: 13px;
  opacity: 0.7;
}

.email-option__pending {
  margin-left: auto;
  font-size: 12px;
  opacity: 0.6;
}

.email-option--new {
  font-style: italic;
  font-size: 13px;
}
</style>